import { useEffect, useState } from 'react'
import type { RecordModel } from 'pocketbase'
import { pb } from '../../lib/pb'
import { shortDate } from '../../lib/format'
import {
  listAnnouncements,
  createAnnouncement,
  saveAnnouncement,
  publishAnnouncement,
  deleteAnnouncement,
  type AnnouncementChannel,
  type AnnouncementDraft,
  type AnnouncementSeverity,
} from '../../lib/adminApi'
import { Card, Button, Input, Select, Badge, Modal, Skeleton } from '../../components/ui'
import { EmptyState } from '../../components/EmptyState'
import '../pages.css'
import './admin.css'

const CHANNELS: { value: AnnouncementChannel; label: string; note?: string }[] = [
  { value: 'banner', label: 'Console banner' },
  { value: 'bell', label: 'Alert bell' },
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'iOS push', note: 'follow-on' },
  { value: 'marketing', label: 'Marketing site', note: 'follow-on' },
]
const SEVERITY_COLOR: Record<AnnouncementSeverity, string> = {
  info: 'var(--c-accent)',
  warn: 'var(--c-warn)',
  critical: 'var(--c-error)',
}

const emptyDraft = (): AnnouncementDraft => ({
  title: '',
  body: '',
  channels: ['banner'],
  audience: 'all',
  severity: 'info',
  dismissible: true,
})

const toDraft = (r: RecordModel): AnnouncementDraft => ({
  id: r.id,
  title: r.title,
  body: r.body || '',
  channels: Array.isArray(r.channels) ? r.channels : [],
  audience: r.audience || 'all',
  audiencePlan: r.audiencePlan || '',
  audienceAccountId: r.audienceAccountId || '',
  severity: r.severity || 'info',
  expiresAt: r.expiresAt || '',
  dismissible: r.dismissible !== false,
})

export function AdminAnnouncements() {
  const [rows, setRows] = useState<RecordModel[] | null>(null)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<AnnouncementDraft | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = () =>
    listAnnouncements(pb.authStore.token)
      .then((d) => setRows(d.announcements))
      .catch((e) => setError(e.message))

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async () => {
    if (!editing) return
    setBusy(true)
    try {
      if (editing.id) await saveAnnouncement(editing, pb.authStore.token)
      else await createAnnouncement(editing, pb.authStore.token)
      setEditing(null)
      await reload()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const publish = async (id: string) => {
    setBusy(true)
    try {
      const { delivery } = await publishAnnouncement(id, pb.authStore.token)
      if (delivery.email) console.log(`announcement emailed to ${delivery.email} recipients`)
      await reload()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    setBusy(true)
    try {
      await deleteAnnouncement(id, pb.authStore.token)
      await reload()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const toggleChannel = (c: AnnouncementChannel) =>
    setEditing((d) =>
      d
        ? { ...d, channels: d.channels.includes(c) ? d.channels.filter((x) => x !== c) : [...d.channels, c] }
        : d,
    )

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Admin · Announcements</h1>
          <p className="page__sub">
            Publish a platform-wide message. Banner + bell show in the console instantly; email fans out on publish.
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing(emptyDraft())}>
          New announcement
        </Button>
      </div>

      {error && (
        <Card style={{ marginBottom: '1rem' }}>
          <p style={{ color: 'var(--c-error)' }}>{error}</p>
        </Card>
      )}

      {!rows ? (
        <Card>
          <Skeleton w="100%" h={18} style={{ marginBottom: 12 }} />
          <Skeleton w="80%" h={18} />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState title="No announcements" body="Create one to message every account at once." />
        </Card>
      ) : (
        <Card>
          <div className="ann-list">
            {rows.map((r) => (
              <div key={r.id} className="ann-row" style={{ borderTop: '1px solid var(--hairline)', paddingTop: '0.75rem' }}>
                <div className="ann-row__main">
                  <div className="ann-row__title">{r.title}</div>
                  <div className="ann-row__body">{r.body}</div>
                  <div className="ann-row__meta">
                    <Badge color={r.status === 'published' ? 'var(--cred-live)' : 'var(--c-sub)'} dot>
                      {r.status}
                    </Badge>
                    <Badge color={SEVERITY_COLOR[r.severity as AnnouncementSeverity] ?? 'var(--c-sub)'}>
                      {r.severity}
                    </Badge>
                    {(Array.isArray(r.channels) ? r.channels : []).map((c: string) => (
                      <Badge key={c}>{c}</Badge>
                    ))}
                    {r.publishedAt && (
                      <span className="admin-table__sub">published {shortDate(r.publishedAt)}</span>
                    )}
                  </div>
                </div>
                <div className="ann-row__actions">
                  {r.status !== 'published' && (
                    <Button size="sm" variant="primary" disabled={busy} onClick={() => publish(r.id)}>
                      Publish
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setEditing(toDraft(r))}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" disabled={busy} onClick={() => remove(r.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit announcement' : 'New announcement'}
        wide
      >
        {editing && (
          <div className="ann-form">
            <Input
              label="Title"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            />
            <div className="field">
              <label className="field__label">Message</label>
              <textarea
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                placeholder="What do you want every account to see?"
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Select
                label="Severity"
                value={editing.severity}
                onChange={(e) => setEditing({ ...editing, severity: e.target.value as AnnouncementSeverity })}
                options={[
                  { value: 'info', label: 'Info' },
                  { value: 'warn', label: 'Warning' },
                  { value: 'critical', label: 'Critical' },
                ]}
              />
              <Select
                label="Audience"
                value={editing.audience}
                onChange={(e) =>
                  setEditing({ ...editing, audience: e.target.value as AnnouncementDraft['audience'] })
                }
                options={[
                  { value: 'all', label: 'Everyone' },
                  { value: 'plan', label: 'A plan' },
                  { value: 'account', label: 'One account' },
                ]}
              />
              {editing.audience === 'plan' && (
                <Select
                  label="Plan"
                  value={editing.audiencePlan || 'free'}
                  onChange={(e) => setEditing({ ...editing, audiencePlan: e.target.value })}
                  options={['free', 'solo', 'team', 'scale', 'enterprise', 'black'].map((p) => ({
                    value: p,
                    label: p,
                  }))}
                />
              )}
              {editing.audience === 'account' && (
                <Input
                  label="Account id"
                  value={editing.audienceAccountId || ''}
                  onChange={(e) => setEditing({ ...editing, audienceAccountId: e.target.value })}
                />
              )}
            </div>

            <div className="field">
              <label className="field__label">Channels</label>
              <div className="ann-checks">
                {CHANNELS.map((c) => (
                  <label key={c.value} className="ann-check">
                    <input
                      type="checkbox"
                      checked={editing.channels.includes(c.value)}
                      onChange={() => toggleChannel(c.value)}
                    />
                    {c.label}
                    {c.note && <small style={{ color: 'var(--c-faint)' }}> ({c.note})</small>}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Input
                label="Expires (optional)"
                type="date"
                value={editing.expiresAt ? editing.expiresAt.slice(0, 10) : ''}
                onChange={(e) =>
                  setEditing({ ...editing, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : '' })
                }
              />
              <label className="ann-check">
                <input
                  type="checkbox"
                  checked={editing.dismissible !== false}
                  onChange={(e) => setEditing({ ...editing, dismissible: e.target.checked })}
                />
                Banner dismissible
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.5rem' }}>
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button variant="primary" loading={busy} onClick={save}>
                {editing.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
